import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import MatureContentService from '@/services/mature-content-service';

export interface FeedItem {
  id: string;
  user_id: string;
  image_url: string | null;
  thumbnail_url: string | null;
  is_relic: boolean | null;
  is_mature?: boolean | null;
  created_at: string;
  like_count?: number | null; // Read from loops
  comment_count?: number | null; // Read from loops
  view_count?: number | null; // Read from loops
  auto_category?: string | null;
  user_category?: string | null;
  // Added for inspect UI
  content?: string | null;
  content_labels?: string[] | null;
  file_type?: string | null;
  image_version?: number | null;
  saved_at?: string | null;
  expires_at?: string | null;
  loop_id?: string | null;
}

interface UseFeedOptions {
  mode: 'bulletins' | 'relics';
  category: string;
  pageSize?: number;
  // New: explicitly control time window for bulletins view
  timeWindow?: 'chrono24h' | 'all';
  // Optional: control which moderation statuses to include (test page may include 'pending')
  moderationStatuses?: ('approved' | 'flagged' | 'pending')[];
  // Include rows where moderation_status is null (legacy/unmoderated rows)
  includeNullModeration?: boolean;
}

export function useBulletinsFeedV2({ mode, category, pageSize = 24, timeWindow = 'all', moderationStatuses = ['approved', 'flagged'], includeNullModeration = false }: UseFeedOptions) {
  const [items, setItems] = useState<FeedItem[]>([]);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const matureAllowed = useMemo(() => MatureContentService.shouldShowMatureContent(), []);

  const fetchPage = useCallback(async (pageIndex: number, replace = false) => {
    setLoading(true);
    try {
      const from = pageIndex * pageSize;
      const to = from + pageSize - 1;

      const last24h = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();

      // Join with loops to get engagement data
      let query = supabase
        .from('bulletins')
        .select(`
          id,user_id,image_url,thumbnail_url,is_relic,is_mature,created_at,auto_category,user_category,moderation_status,content,content_labels,file_type,image_version,saved_at,expires_at,loop_id,
          loops!bulletins_loop_id_fkey(
            like_count,
            comment_count,
            view_count
          )
        `)
        .order('created_at', { ascending: false })
        .range(from, to);

      if (mode === 'relics') {
        query = query.eq('is_relic', true);
      } else {
        if (timeWindow === 'chrono24h') {
          // Chronological last 24h for bulletins
          query = query.gte('created_at', last24h);
        } else {
          // All-time for test view: include all posts (bulletins + relics)
          // No additional filter; rely on category/moderation filters below.
        }
      }

      // Include configured moderation statuses (defaults to approved/flagged)
      if (includeNullModeration) {
        const ors = [
          ...moderationStatuses.map((s) => `moderation_status.eq.${s}`),
          'moderation_status.is.null',
        ].join(',');
        query = query.or(ors as any);
      } else {
        query = query.in('moderation_status', moderationStatuses as any);
      }

      // Category filter
      if (category && category !== 'all') {
        console.log('[Feed] Filtering by category:', category);
        // Match auto_category or user_category (case-insensitive)
        // For content_labels array, try multiple case variations
        const lc = category.toLowerCase();
        const titleCase = lc.charAt(0).toUpperCase() + lc.slice(1);
        const orParts = [
          `auto_category.ilike.${category}`,
          `user_category.ilike.${category}`,
          `content_labels.cs.["${category}"]`, // Original
          `content_labels.cs.["${lc}"]`, // lowercase
          `content_labels.cs.["${titleCase}"]`, // Title Case (most common from Vision API)
        ];
        const orClause = orParts.join(',');
        console.log('[Feed] Category filter OR clause:', orClause);
        query = query.or(orClause as any);
      }

      // Mature filter: if not allowed, still fetch but we will blur. Optionally exclude truly explicit flagged? keep as-is.

      const { data, error } = await query;
      if (error) throw error;

      // Map engagement data from loops
      const next = ((data || []) as any[]).filter((d: any) => d.image_url || d.thumbnail_url).map((d: any) => {
        const loop = d.loops;
        return {
          ...d,
          like_count: loop?.like_count ?? 0,
          comment_count: loop?.comment_count ?? 0,
          view_count: loop?.view_count ?? 0,
        };
      }) as FeedItem[];
      // eslint-disable-next-line no-console
      console.log('[useBulletinsFeedV2] fetched', { count: data?.length || 0, pageIndex, replace, mode, category });
      // Debug: log sample content_labels to understand format
      if (data?.length && category !== 'all') {
        // eslint-disable-next-line no-console
        console.log('[DEBUG] Sample content_labels:', data.slice(0, 3).map((d: any) => ({ id: d.id, content_labels: d.content_labels, auto_category: d.auto_category, user_category: d.user_category })));
      }
      setItems((prev) => (replace ? next : [...prev, ...next]));
      setHasMore((data || []).length === pageSize);
    } catch (e) {
      console.error('Feed fetch error', e);
    } finally {
      setLoading(false);
    }
  }, [mode, category, pageSize, timeWindow, moderationStatuses, includeNullModeration]);

  const refresh = useCallback(async () => {
    setPage(0);
    await fetchPage(0, true);
  }, [fetchPage]);

  const loadMore = useCallback(async () => {
    if (loading || !hasMore) return;
    const next = page + 1;
    setPage(next);
    await fetchPage(next);
  }, [fetchPage, page, loading, hasMore]);

  useEffect(() => {
    // Full refresh on deps change
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, category, timeWindow, moderationStatuses, includeNullModeration]);

  return { items, loading, hasMore, loadMore, refresh, empty: !loading && items.length === 0 };
}
